// src/pages/Login/LoginOptions.js - FIXED VERSION
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import sessionManager from '../../utilities/SessionManager'; // FIXED: Removed 's' from SessionsManager
import {
    ArrowLeft,
    User,
    Shield,
    Eye,
    EyeOff,
    Loader2,
    UserCheck,
    Settings
} from 'lucide-react';
import {
    validateUser,
    getEmployeeDetails,
    getMenu,
    getUserRoleDashboardType,
    clearErrors,
    clearSuccess,
    logout
} from '../../slices/auth/authSlice';
import ThemeToggle from '../../components/ThemeToggle';
import ForgotPasswordModal from '../../components/ForgotPasswordModal';
import ChangePasswordModal from '../../components/ChangePasswordModal';

// Validation Schema for Role Login
const roleValidationSchema = Yup.object({
    password: Yup.string()
        .min(6, 'Password must be at least 6 characters')
        .required('Role password is required')
});

const LoginOptions = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [showRolePassword, setShowRolePassword] = useState(false);
    const [isProcessingLogin, setIsProcessingLogin] = useState(false); // ADDED: Track login processing
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [pendingRoleId, setPendingRoleId] = useState(null); // Role login succeeded but password change is required first

    const {
        employeeId,
        loading,
        error,
        success,
        isAuthenticated,
        roleData,
        roleId,
        userData
    } = useSelector((state) => state.auth);

    // Redirect if not authenticated (shouldn't happen, but good safeguard)
    useEffect(() => {
        if (!isAuthenticated || !employeeId) {
            console.log('❌ No authentication found on login options page - redirecting to login');
            navigate('/');
        }
    }, [isAuthenticated, employeeId, navigate]);

    // Role login form
    const roleFormik = useFormik({
        initialValues: {
            password: ''
        },
        validationSchema: roleValidationSchema,
        onSubmit: async (values) => {
            setIsProcessingLogin(true); // ADDED: Set processing state
            
            const credentials = {
                employeeId: employeeId,
                password: values.password
            };

            try {
                console.log('🔍 Attempting role validation...');
                const userResult = await dispatch(validateUser(credentials)).unwrap();

                console.log('✅ User validation result:', userResult);
                console.log('🎯 Extracted roleId:', userResult.roleId);

                if (userResult.data?.IsFirstTimeLogin) {
                    console.log('🔑 First-time role login detected - requiring password change');
                    setPendingRoleId(userResult.roleId);
                    setIsProcessingLogin(false);
                    return;
                }

                if (userResult.roleId) {
                    console.log('🔍 Calling getMenu with roleId:', userResult.roleId);
                    await dispatch(getMenu(userResult.roleId)).unwrap();
                    // Not unwrapped — a failure here shouldn't block login, RoleBasedApplication
                    // falls back to the Base dashboard if this doesn't resolve.
                    dispatch(getUserRoleDashboardType(userResult.roleId));
                    toast.success('Role login successful!');
                } else {
                    toast.error('Role ID not found in response');
                    setIsProcessingLogin(false); // ADDED: Reset processing state on error
                }
            } catch (error) {
                console.error('❌ Role login error:', error);
                setIsProcessingLogin(false); // ADDED: Reset processing state on error
                // Error is already handled by the thunk and shown in UI
            }
        }
    });

    // Handle success states - IMPROVED: Better session timing
    useEffect(() => {
        if (success.getEmployeeDetails && !isProcessingLogin) {
            console.log('✅ Employee login successful - setting up session');
            dispatch(clearSuccess());

            // IMPROVED: Setup session data properly before navigation
            setTimeout(() => {
                const authData = {
                    employeeId: employeeId,
                    loginType: 'employee'
                };

                sessionManager.login(authData);
                console.log('✅ Employee session setup complete - navigating to dashboard');
                navigate('/employee-dashboard');
            }, 100); // Small delay to ensure Redux state is updated
        }
    }, [success.getEmployeeDetails, dispatch, navigate, employeeId, isProcessingLogin]);

    useEffect(() => {
        if (success.getMenu && isProcessingLogin) {
            console.log('✅ Role login successful - setting up session');
            dispatch(clearSuccess());

            // IMPROVED: Manually sync session data BEFORE navigation
            const syncSessionAndNavigate = () => {
                const authData = {
                    employeeId: employeeId,
                    loginType: 'role',
                    roleData: roleData,
                    roleId: roleId,
                    userData: userData
                };

                // Manually call sessionManager.login to ensure immediate sync
                sessionManager.login(authData);
                console.log('✅ Session manually synced before navigation');
                
                // Navigate after session is definitely set up
                setTimeout(() => {
                    console.log('✅ Role session setup complete - navigating to dashboard');
                    setIsProcessingLogin(false);
                    navigate('/role-dashboard');
                }, 50); // Minimal delay just to ensure sessionManager.login completes
            };

            // Small delay to ensure Redux state is fully updated first
            setTimeout(syncSessionAndNavigate, 100);
        }
    }, [success.getMenu, dispatch, navigate, isProcessingLogin, employeeId, roleData, roleId, userData]);

    // IMPROVED: Handle employee login with better session management
    const handleEmployeeLogin = async () => {
        setIsProcessingLogin(true); // ADDED: Set processing state
        
        try {
            await dispatch(getEmployeeDetails(employeeId)).unwrap();
            toast.success('Employee login successful!');
            setIsProcessingLogin(false);
        } catch (error) {
            console.error('Employee details error:', error);
            setIsProcessingLogin(false);
        }
    };

    const handleBackToLogin = () => {
        console.log('🔄 Going back to login - clearing session');
        sessionManager.clearSession();
        dispatch(logout());
        dispatch(clearErrors());
        dispatch(clearSuccess());
        navigate('/');
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-100 via-white to-orange-50 dark:from-gray-900 dark:via-[#0d1b5e] dark:to-gray-900 flex items-center justify-center p-4 transition-colors relative overflow-hidden">

            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-30 dark:opacity-10">
                <div className="absolute top-0 left-0 w-full h-full" style={{
                    backgroundImage: `radial-gradient(circle at 25% 25%, rgba(13,27,94,0.12) 0%, transparent 50%),
                                     radial-gradient(circle at 75% 75%, rgba(234,88,12,0.10) 0%, transparent 50%)`
                }}></div>
            </div>

            {/* Floating shapes */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-1/4 left-1/4 w-48 h-48 bg-gradient-to-r from-[#0d1b5e] to-blue-800 rounded-full opacity-10 blur-3xl animate-pulse"></div>
                <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-gradient-to-r from-orange-500 to-orange-300 rounded-full opacity-10 blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
            </div>

            {/* Theme Toggle */}
            <div className="absolute top-4 right-4 z-10">
                <ThemeToggle variant="simple" showLabel={false} />
            </div>

            {/* Back Button */}
            <button
                onClick={handleBackToLogin}
                disabled={isProcessingLogin} // ADDED: Disable during processing
                className="absolute top-4 left-4 z-10 flex items-center space-x-2 px-3 py-2 bg-white/20 dark:bg-gray-900/40 backdrop-blur-sm rounded-lg text-gray-700 dark:text-gray-200 hover:bg-white/30 dark:hover:bg-gray-900/60 transition-all duration-200 border border-white/30 dark:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm">Back to Login</span>
            </button>

            {/* Main Container Card - Made more compact */}
            <div className="w-full max-w-3xl bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden border border-white/20 dark:border-gray-700 backdrop-blur-sm transition-colors max-h-[95vh] overflow-y-auto">

                {/* Header - Made more compact */}
                <div className="bg-[#0d1b5e] dark:bg-[#0a1240] px-4 py-3 text-white text-center transition-colors">
                    <h1 className="text-lg sm:text-xl font-bold mb-0.5">Choose Your Access Level</h1>
                    <p className="text-orange-200 dark:text-orange-300 text-xs mb-2">Select how you want to proceed with your session</p>
                    <div className="flex items-center justify-center space-x-3 text-xs">
                        <span>Employee ID: <span className="font-semibold">{employeeId}</span></span>
                        <div className="w-1 h-3 bg-orange-300 rounded-full"></div>
                        <span>Authenticated Successfully</span>
                    </div>
                </div>

                {/* Options Grid - Reduced padding and spacing */}
                <div className="grid grid-cols-1 lg:grid-cols-2">

                    {/* Left Side - Employee Portal */}
                    <div className="p-4 border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1e2535] transition-colors">
                        <div className="text-center mb-3">
                            <div className="mx-auto w-10 h-10 bg-gradient-to-r from-[#0d1b5e] to-blue-900 rounded-xl flex items-center justify-center mb-2 shadow-lg transform hover:scale-105 transition-transform">
                                <User className="w-5 h-5 text-white" />
                            </div>
                            <h2 className="text-base font-bold text-[#0d1b5e] dark:text-white mb-1">
                                Employee Portal
                            </h2>
                            <p className="text-[#0d1b5e]/70 dark:text-white/70 text-xs">
                                Access your personal employee information and details
                            </p>
                        </div>

                        <div className="space-y-2.5">
                            <div className="bg-white dark:bg-gray-800 border border-[#0d1b5e]/15 dark:border-white/10 rounded-lg p-2.5 transition-colors">
                                <h3 className="font-semibold text-[#0d1b5e] dark:text-white mb-1.5 text-xs">What you can access:</h3>
                                <ul className="text-[#0d1b5e]/80 dark:text-white/80 space-y-1 text-xs">
                                    <li className="flex items-center space-x-2">
                                        <div className="w-1.5 h-1.5 bg-orange-500 rounded-full flex-shrink-0"></div>
                                        <span>Personal Information</span>
                                    </li>
                                    <li className="flex items-center space-x-2">
                                        <div className="w-1.5 h-1.5 bg-orange-500 rounded-full flex-shrink-0"></div>
                                        <span>Employment Details</span>
                                    </li>
                                    <li className="flex items-center space-x-2">
                                        <div className="w-1.5 h-1.5 bg-orange-500 rounded-full flex-shrink-0"></div>
                                        <span>Pay Stubs & Benefits</span>
                                    </li>
                                    <li className="flex items-center space-x-2">
                                        <div className="w-1.5 h-1.5 bg-orange-500 rounded-full flex-shrink-0"></div>
                                        <span>Time & Attendance</span>
                                    </li>
                                </ul>
                            </div>

                            <button
                                onClick={handleEmployeeLogin}
                                disabled={loading.getEmployeeDetails || isProcessingLogin} // IMPROVED: Better disabled state
                                className="w-full bg-gradient-to-r from-blue-900 to-orange-500 hover:from-blue-950 hover:to-orange-600 text-white py-2 px-4 rounded-lg font-semibold shadow-lg hover:shadow-xl focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-[#1e2535] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98]"
                            >
                                {(loading.getEmployeeDetails || isProcessingLogin) ? (
                                    <div className="flex items-center justify-center space-x-2">
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        <span className="text-xs">Loading...</span>
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center space-x-2">
                                        <UserCheck className="w-4 h-4" />
                                        <span className="text-xs">Continue as Employee</span>
                                    </div>
                                )}
                            </button>

                            {error.getEmployeeDetails && (
                                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg p-2 transition-colors">
                                    <p className="text-red-600 dark:text-red-300 text-xs text-center">
                                        {error.getEmployeeDetails}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Side - Role Access */}
                    <div className="p-4 bg-[#0d1b5e]/5 dark:bg-[#1e2535] transition-colors">
                        <div className="text-center mb-3">
                            <div className="mx-auto w-10 h-10 bg-gradient-to-r from-[#0d1b5e] to-orange-500 rounded-xl flex items-center justify-center mb-2 shadow-lg transform hover:scale-105 transition-transform">
                                <Shield className="w-5 h-5 text-white" />
                            </div>
                            <h2 className="text-base font-bold text-[#0d1b5e] dark:text-white mb-1">
                                Role Access Portal
                            </h2>
                            <p className="text-[#0d1b5e]/70 dark:text-white/70 text-xs">
                                Login with your role credentials to access advanced system features
                            </p>
                        </div>

                        <div className="space-y-2.5">
                            <div className="bg-white dark:bg-gray-800 border border-[#0d1b5e]/15 dark:border-white/10 rounded-lg p-2.5 transition-colors">
                                <h3 className="font-semibold text-[#0d1b5e] dark:text-white mb-1.5 text-xs">Role-based access to:</h3>
                                <ul className="text-[#0d1b5e]/80 dark:text-white/80 space-y-1 text-xs">
                                    <li className="flex items-center space-x-2">
                                        <div className="w-1.5 h-1.5 bg-orange-500 rounded-full flex-shrink-0"></div>
                                        <span>Company Reports & Analytics</span>
                                    </li>
                                    <li className="flex items-center space-x-2">
                                        <div className="w-1.5 h-1.5 bg-orange-500 rounded-full flex-shrink-0"></div>
                                        <span>Role-specific Modules</span>
                                    </li>
                                    <li className="flex items-center space-x-2">
                                        <div className="w-1.5 h-1.5 bg-orange-500 rounded-full flex-shrink-0"></div>
                                        <span>Voucher Management</span>
                                    </li>
                                    <li className="flex items-center space-x-2">
                                        <div className="w-1.5 h-1.5 bg-orange-500 rounded-full flex-shrink-0"></div>
                                        <span>Work Order/PO Management</span>
                                    </li>
                                    <li className="flex items-center space-x-2">
                                        <div className="w-1.5 h-1.5 bg-orange-500 rounded-full flex-shrink-0"></div>
                                        <span>Inventory Management</span>
                                    </li>
                                </ul>
                            </div>

                            {/* Role Password Form */}
                            <form onSubmit={roleFormik.handleSubmit} className="space-y-2">
                                <div className="space-y-1">
                                    <label htmlFor="rolePassword" className="block text-xs font-semibold text-[#0d1b5e] dark:text-white/80">
                                        Role Password
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showRolePassword ? 'text' : 'password'}
                                            id="rolePassword"
                                            name="password"
                                            value={roleFormik.values.password}
                                            onChange={roleFormik.handleChange}
                                            onBlur={roleFormik.handleBlur}
                                            className={`w-full pl-3 pr-10 py-2 border-2 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-colors bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 text-xs ${roleFormik.touched.password && roleFormik.errors.password
                                                ? 'border-red-300 dark:border-red-500 bg-red-50 dark:bg-red-900/20'
                                                : 'border-[#0d1b5e]/25 dark:border-gray-500'
                                                }`}
                                            placeholder="Enter your role password"
                                            disabled={loading.validateUser || loading.getMenu || isProcessingLogin} // IMPROVED: Better disabled state
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowRolePassword(!showRolePassword)}
                                            className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                            disabled={loading.validateUser || loading.getMenu || isProcessingLogin} // IMPROVED: Better disabled state
                                        >
                                            {showRolePassword ? (
                                                <EyeOff className="h-4 w-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" />
                                            ) : (
                                                <Eye className="h-4 w-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" />
                                            )}
                                        </button>
                                    </div>
                                    {roleFormik.touched.password && roleFormik.errors.password && (
                                        <p className="text-red-600 dark:text-red-300 text-xs">{roleFormik.errors.password}</p>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading.validateUser || loading.getMenu || !roleFormik.isValid || isProcessingLogin} // IMPROVED: Better disabled state
                                    className="w-full bg-gradient-to-r from-blue-900 to-orange-500 hover:from-blue-950 hover:to-orange-600 text-white py-2 px-4 rounded-lg font-semibold shadow-lg hover:shadow-xl focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-[#1e2535] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98]"
                                >
                                    {(loading.validateUser || loading.getMenu || isProcessingLogin) ? (
                                        <div className="flex items-center justify-center space-x-2">
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            <span className="text-xs">Authenticating...</span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center justify-center space-x-2">
                                            <Settings className="w-4 h-4" />
                                            <span className="text-xs">Access Role Portal</span>
                                        </div>
                                    )}
                                </button>

                                {(error.validateUser || error.getMenu) && (
                                    <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg p-2 transition-colors">
                                        <p className="text-red-600 dark:text-red-300 text-xs text-center">
                                            {error.validateUser || error.getMenu}
                                        </p>
                                    </div>
                                )}

                                <div className="text-center">
                                    <button
                                        type="button"
                                        onClick={() => setShowForgotPassword(true)}
                                        className="text-xs text-orange-600 dark:text-orange-400 hover:text-orange-500 dark:hover:text-orange-300 font-medium"
                                    >
                                        Forgot password?
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        <ForgotPasswordModal
            isOpen={showForgotPassword}
            onClose={() => setShowForgotPassword(false)}
            loginType="role"
        />
        <ChangePasswordModal
            isOpen={!!pendingRoleId}
            username={employeeId}
            loginType="role"
            onSuccess={async () => {
                setIsProcessingLogin(true);
                try {
                    await dispatch(getMenu(pendingRoleId)).unwrap();
                    dispatch(getUserRoleDashboardType(pendingRoleId));
                    toast.success('Role login successful!');
                } catch (err) {
                    console.error('❌ Role login error after password change:', err);
                    setIsProcessingLogin(false);
                } finally {
                    setPendingRoleId(null);
                }
            }}
        />
        </div>
    );
};

export default LoginOptions;